import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Carousel, Slide } from '@abyss/web/ui/Carousel';
import { useTranslation } from 'react-i18next';
import { styled } from '@abyss/web/tools/styled';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { Heading } from '@abyss/web/ui/Heading';
import { Button } from '@abyss/web/ui/Button';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { ContentWrapper } from '../ContentWrapper';
import { benefitsCarouselScreen,phoneOnly } from '../ConstantsStyles';
import { slideContainerStyle } from '../MyBenefits/MyBenefits.style';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { ContainerForAllSectionsStyled } from '../ProviderDetailTabs/ProviderDetailTabs.styled';
import { carouselStyle } from '../MyBenefits/MyBenefits.style';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useChipStore } from '../../store/useChipStore';
import { useShallow } from 'zustand/react/shallow';
import { ChipState } from '../../store/useChipStore/chipStore';

interface NonUhcVisionProps {
  id: string;
  key: string;
  title: string;
  content: string;
  hyperlink: string;
  cta: string;
  chipValue: string;
  hpbpCode: string;
}
interface Props {
  NonUhcVisionContent: NonUhcVisionProps[];
  isPsxHome?: boolean;
}

interface BoxContentProps {
  nonUhcVisionData: NonUhcVisionProps;
  index: number;
}
export const CardButton = {
  'abyss-button-root': {
    '@screen < $sm': {
      fontWeight: '$semibold',
    },
  },
};
export const ContentCardsContainer = styled('div', {
  dynamic: ({ cssProps: { backgroundColor } }) => ({
    backgroundColor,
  }),
  height: '100%',
  padding: '$md 24px',
  borderRadius: '8px',
  '@screen < $sm': {
    padding: '$md 24px',
    paddingLeft: 0,
    minHeight: '174px',
  },
});
export const NonUhcVisionPage = ({ NonUhcVisionContent,isPsxHome }: Props) => {
  const displayCarousel = useMediaQuery(benefitsCarouselScreen);
  const BoxContent = ({ nonUhcVisionData, index }: BoxContentProps) => {
    const { t } = useTranslation();
    const {
      title,
      content,
      hyperlink,
      cta,
    } = nonUhcVisionData;

    const cardsBackgrounds = { 0: '$tint2', 1: '$pastel3', 2: '$pastel4' };

    const datatestid = title.replace(/[^a-zA-Z ]/g, '').replace(/\s+/g, '-');
  const mobileScreen = useMediaQuery(phoneOnly);

  const { chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      chipValue: state.chipValue,
    }))
  );

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: "vision overview"
  });

  const hasTrackedVisionEvent = useRef(false);
  useEffect(() => {
    const debouncedTrackEvent = debounce(() => {
      if (!hasTrackedVisionEvent.current && isPsxHome) {
        adobePageTrackEvent();
        hasTrackedVisionEvent.current = true;
      }
    }, 200);

    debouncedTrackEvent();
    return () => {
      debouncedTrackEvent.cancel();
    };
  }, [chipValue]);

  const analyticsInfo = {
    name: cta,
    location: "body:about vision coverage",
    destinationUrl: hyperlink,
  };
 const handleLinkTrackEvent = () => {
    adobeLinkTrackEvent({
      ...analyticsInfo,
    });

    window.open(hyperlink, '_blank');
  };
    return (

      <ContentCardsContainer cssProps={{ backgroundColor: cardsBackgrounds[index % 3]
      }}>
            <Layout.Group
              alignItems="top"
              css={{
                '&, > div': {
                  height: '100%',
                  width:"384px",
                },
              }}
              data-auto-testid="non-uhc-vision-card"
              data-testid="non-uhc-vision-card"
            >

              <Layout.Stack
                alignItems="left"
                css={{
                  'div:nth-child(2)': {
                    flexGrow: 1,
                    paddingBottom: '8px',
                  }
                }}
              >
                <Heading
                  data-auto-testid={`${datatestid}-heading`}
                  data-testid={`${datatestid}-heading`}
                  display="h6"
                  offset={2}
                >
                  {t(title)}
                </Heading>
                <Text
                  color="$gray8"
                  data-auto-testid={`${datatestid}-content`}
                  data-testid={`${datatestid}-content`}
                  size={mobileScreen ? '$sm' : '$md'}
                >
                  {content} {" "}
                  <Text as="span" css={{fontWeight: '$bold' }}>United Healthcare.</Text>
                </Text>
                <Button
                  after={
                    <IconSymbol icon="open_in_new" />
                  }
                  aria-label={`${cta}`.toLowerCase()}
                  css={CardButton}
                  data-auto-testid={`${datatestid}-button`}
                  data-testid={`${datatestid}-button`}
                  name="get_started"
                  onClick={handleLinkTrackEvent}
                >
                  <Text color="$white" size={mobileScreen ? '$sm' : '$md'}>
                    {cta}
                  </Text>

                </Button>
              </Layout.Stack>
            </Layout.Group>
          </ContentCardsContainer>

    );
  };

  const slides = NonUhcVisionContent
    ?.map((nonUhcVisionData, i) => (
        <Slide key={nonUhcVisionData.key}>
          <Slide.Container css={slideContainerStyle}>
            <BoxContent  index={i} nonUhcVisionData={nonUhcVisionData} />
          </Slide.Container>
        </Slide>
      ))
    .filter(Boolean); // filter out null values

  // If there are no slides to show, don't render the Carousel
  if (slides.length === 0) {
    return null;
  }

  return (

<ContainerForAllSectionsStyled
      data-auto-testid={`my-benefits-section`}
      data-testid={`my-benefits-section`}
      height="auto"
    >
      <ContentWrapper>
     <Carousel
          css={
            (displayCarousel || NonUhcVisionContent.length > 2) &&
            NonUhcVisionContent.length !== 1
              ? carouselStyle
              : {
                  ...carouselStyle,
                  'abyss-carousel-minimal-controls': {
                    display: 'none',
                  },
                }
          }
          key={`benefit-data-${NonUhcVisionContent ? NonUhcVisionContent.length : 0}`}
          minimal
          noLoop
          slides={slides}
          slidesPerView={1}
          variant="none"
        />
    </ContentWrapper>
    </ContainerForAllSectionsStyled>
  );
};
